import * as React from 'react';
import { format } from 'date-fns';
import { CalendarIcon, X, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getDateFnsLocale } from '@/lib/utils/date';

export interface DateTimePickerProps {
  value?: Date | string | null | undefined;
  /** Receives `null` when the user clears the field (see `handleClear`). */
  onChange: (date: Date | null | undefined) => void;
  mode?: 'datetime' | 'date';
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

export function DateTimePicker({
  value,
  onChange,
  mode = 'datetime',
  disabled = false,
  placeholder,
  className,
  minDate,
  maxDate,
}: DateTimePickerProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const [open, setOpen] = React.useState(false);
  const hourRef = React.useRef<HTMLDivElement>(null);
  const minuteRef = React.useRef<HTMLDivElement>(null);

  // Normalize value (convert string → Date)
  const parsedValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }, [value]);

  const currentHour = parsedValue?.getHours() ?? new Date().getHours();
  const currentMinute = parsedValue?.getMinutes() ?? 0;

  // Auto-scroll to selected time when popover opens
  React.useEffect(() => {
    if (open && mode === 'datetime') {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Scroll hour into view
        if (hourRef.current) {
          const hourButton = hourRef.current.querySelector(`[data-hour="${currentHour}"]`);
          if (hourButton) {
            hourButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        // Scroll minute into view
        if (minuteRef.current) {
          const minuteButton = minuteRef.current.querySelector(`[data-minute="${currentMinute}"]`);
          if (minuteButton) {
            minuteButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, currentHour, currentMinute, mode]);

  // Handle date change
  const handleDateChange = React.useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      if (mode === 'date') {
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 0);
        onChange(endOfDay);
      } else {
        const current = parsedValue ?? new Date();
        const updated = new Date(date);
        updated.setHours(current.getHours());
        updated.setMinutes(current.getMinutes());
        onChange(updated);
      }
    },
    [mode, onChange, parsedValue]
  );

  // Handle time selection
  const handleTimeSelect = React.useCallback(
    (type: 'hour' | 'minute', val: number) => {
      const current = parsedValue ?? new Date();
      const updated = new Date(current);
      if (type === 'hour') updated.setHours(val);
      if (type === 'minute') updated.setMinutes(val);
      onChange(updated);
    },
    [onChange, parsedValue]
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Clear with `null`, never `undefined`. react-hook-form resolves a controlled
      // field with `get(formValues, name, defaultValueCapturedAtMount)`, and its
      // `get` treats `undefined` as "absent" and returns that default — so
      // `onChange(undefined)` handed the old date straight back and the field
      // visibly refused to clear. `null` is a real value and clears it.
      onChange(null);
    },
    [onChange]
  );

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const defaultPlaceholder = mode === 'date' ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';

  const showClear = !!parsedValue && !disabled;

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger disabled={disabled} asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-between pl-3 text-left font-normal',
              !parsedValue && 'text-muted-foreground',
              disabled && 'pointer-events-none opacity-70 bg-slate-100 text-slate-500'
            )}
          >
            <span className={cn('flex-1 truncate', showClear && 'pr-5')}>
              {parsedValue ? (
                mode === 'date'
                  ? format(parsedValue, 'dd/MM/yyyy', { locale: dateFnsLocale })
                  : format(parsedValue, 'dd/MM/yyyy HH:mm', { locale: dateFnsLocale })
              ) : (
                <span>{placeholder ?? defaultPlaceholder}</span>
              )}
            </span>
            <CalendarIcon className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start" side="top">
          <div className="flex">
            {/* Calendar */}
            <div className="p-3 border-r">
              <Calendar
                mode="single"
                selected={parsedValue}
                onSelect={handleDateChange}
                captionLayout="dropdown"
                fromYear={1950}
                toYear={2050}
                {...(minDate || maxDate
                  ? {
                      disabled: [
                        ...(minDate ? [{ before: minDate }] : []),
                        ...(maxDate ? [{ after: maxDate }] : []),
                      ],
                    }
                  : {})}
                initialFocus
              />
            </div>

            {/* Time Picker */}
            {mode === 'datetime' && (
              <div className="flex flex-col p-3 w-[160px]">
                <div className="flex items-center gap-1 mb-2 text-sm font-medium text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{t('timeLabel')}</span>
                </div>
                <div className="flex gap-2">
                  {/* Hours */}
                  <div className="flex flex-col flex-1">
                    <span className="text-xs text-muted-foreground mb-1 text-center">{t('hour')}</span>
                    <div
                      ref={hourRef}
                      className="h-[200px] overflow-y-auto rounded-md border scrollbar-thin"
                    >
                      <div className="p-1">
                        {hours.map((hour) => (
                          <button
                            key={hour}
                            type="button"
                            data-hour={hour}
                            onClick={() => handleTimeSelect('hour', hour)}
                            className={cn(
                              'w-full rounded-md px-2 py-1.5 text-sm transition-colors',
                              hour === currentHour
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'hover:bg-muted'
                            )}
                          >
                            {hour.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Minutes */}
                  <div className="flex flex-col flex-1">
                    <span className="text-xs text-muted-foreground mb-1 text-center">{t('minute')}</span>
                    <div
                      ref={minuteRef}
                      className="h-[200px] overflow-y-auto rounded-md border scrollbar-thin"
                    >
                      <div className="p-1">
                        {minutes.map((minute) => (
                          <button
                            key={minute}
                            type="button"
                            data-minute={minute}
                            onClick={() => handleTimeSelect('minute', minute)}
                            className={cn(
                              'w-full rounded-md px-2 py-1.5 text-sm transition-colors',
                              minute === currentMinute
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'hover:bg-muted'
                            )}
                          >
                            {minute.toString().padStart(2, '0')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick time display */}
                <div className="mt-3 py-2 text-center text-xl font-semibold bg-muted rounded-md">
                  {currentHour.toString().padStart(2, '0')}:
                  {currentMinute.toString().padStart(2, '0')}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Rendered as a SIBLING of the trigger, overlaid in the trigger's right
          padding — never inside it. A <button> nested in the trigger's <button>
          is invalid HTML (React warns about it) and the click did not reliably
          reach it, so clearing the date often did nothing. */}
      {showClear && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={t('clearDate')}
          className="text-muted-foreground hover:text-destructive absolute top-1/2 right-9 z-10 -translate-y-1/2 transition"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
